import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import { get } from 'lodash';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import moment from 'moment';

import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Link from '@material-ui/core/Link';

import CompletedSteps from './CompletedSteps';

export const DownloadCatPlan = gql`
  mutation downloadCatPlan($id: ID!) {
    downloadCatPlan(id: $id)
  }
`;

const SendSeal = gql`
  mutation builderIssueSeal($id: ID!) {
    builderIssueSeal(id: $id)
  }
`;

export const ContinuityStepTab = ({ card, classes, dealTrackingPipeLineId, enqueueSnackbar, updateCard, pipeline }) => {
  const [sealDialogOpen, setSealDialogOpen] = useState(false);

  // --- Data extraction ---
  const stageName       = get(card, 'pipeline_stage.name', '');
  const isApproved      = stageName === 'Approved';
  const cb              = get(card, 'continuity_builder', {});
  const agentCb         = get(card, 'primary_agent.continuity_builder', {});
  const canIssuedSeal   = get(cb, 'canIssuedSeal', false);
  const cbId            = get(cb, 'id');
  const agentId         = get(card, 'primary_agent.id', null);
  const businessType    = get(card, 'primary_agent.company.business_type', '');
  const isRj            = businessType === 'wealth_rj';

  const catPlanFilename          = get(card, 'primary_agent.cat_plan_filename', false);
  const existingPartnerName      = get(agentCb, 'existingPartnerName', '');
  const existingPartnerEmail     = get(agentCb, 'existingPartnerEmail', '');
  const successors               = get(agentCb, 'continuityBuilderSuccessors', []);
  const completedFindPartnerStep = get(agentCb, 'completedFindPartnerStep', false);
  const completedFileAgreement   = get(agentCb, 'completedFileAgreementStep', false);
  const filedAgreementTimestamp  = get(agentCb, 'filedAgreementTimestamp', null);
  const filedAgreementDoc        = get(cb, 'filed_agreement_vault_document', null) || get(agentCb, 'filed_agreement_vault_document', null);
  const accessedDate             = get(agentCb, 'accessedDate', null);
  const lastUpdated              = get(agentCb, 'lastUpdated', null);
  const deal                     = get(agentCb, 'deal', null);
  const userGoal                 = get(agentCb, 'userGoal', null);

  // --- Step completion ---
  const step1Done = !!catPlanFilename;
  const step2Done = completedFindPartnerStep || !!existingPartnerName || successors.length > 0;
  // A voided plan keeps its filed timestamp and agreement document for audit, so
  // neither proves the agreement is still in force. Without this the stepper
  // shows "Filed Agreement" complete on a voided plan.
  const isVoided  = get(cb, 'voided', false) || get(agentCb, 'voided', false);
  const voidedAt  = get(cb, 'voided_at', null) || get(agentCb, 'voided_at', null);

  const step3Done = !isVoided && (completedFileAgreement || !!filedAgreementTimestamp || !!filedAgreementDoc);
  const step4Done = isApproved && !isVoided;

  const stepsDone   = [step1Done, step2Done, step3Done, step4Done];
  const activeStep  = stepsDone.findIndex(d => !d);
  const computedActive = activeStep === -1 ? 4 : activeStep;

  // --- Seal handler ---
  const handleSeal = (issueSeal) => {
    issueSeal({ variables: { id: cbId } })
      .then(() => {
        enqueueSnackbar('Seal has been issued.', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
        setSealDialogOpen(false);
        try {
          const stage = get(pipeline, 'pipeline_stages', []).find(p => p.name === 'Approved');
          if (stage) updateCard(card.id, stage, 1);
        } catch (e) {
          // Stage update is best-effort; seal was already issued
          console.error('Failed to update pipeline stage after seal:', e);
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to issue seal. Please contact administrator.', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      });
  };

  const displayStage = stageName.replace('%20', ' ') || 'Backlog';

  return (
    <div className={classes.root}>

      {/* A voided plan retains its documents and dates — say so, or the retained
          agreement below reads as the current one. */}
      {isVoided && (
        <Typography style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
          <b>Plan voided</b>
          {voidedAt && ` on ${new Date(voidedAt).toLocaleDateString()}`}
          {' — documents and dates below are retained for audit and do not reflect a current agreement.'}
        </Typography>
      )}

      {/* Current stage badge */}
      <div className={classes.stageRow}>
        <Typography className={classes.stageLabel}>Current Stage</Typography>
        <Chip
          label={displayStage}
          size="small"
          className={
            isApproved ? classes.chipApproved
            : step3Done ? classes.chipFiled
            : classes.chipDefault
          }
        />
      </div>

      {/* 4-step vertical stepper */}
      <Stepper activeStep={computedActive} orientation="vertical" className={classes.stepper}>

        {/* Step 1 — Create Plan */}
        <Step completed={step1Done} active={step1Done || computedActive === 0}>
          <StepLabel>Create Plan</StepLabel>
          <StepContent>
            {catPlanFilename ? (
              <div className={classes.stepBody}>
                <Typography variant="body2" className={classes.successText}>
                  Plan on file
                </Typography>
                {isRj && (
                  <Mutation
                    mutation={DownloadCatPlan}
                    onCompleted={() =>
                      enqueueSnackbar('The plan is being retrieved and will be emailed to you.', {
                        variant: 'success',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                      })
                    }
                  >
                    {downloadBuilder => (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        className={classes.inlineBtn}
                        onClick={() => downloadBuilder({ variables: { id: agentId } })}
                      >
                        Email Plan Copy
                      </Button>
                    )}
                  </Mutation>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No plan on file yet.
              </Typography>
            )}
          </StepContent>
        </Step>

        {/* Step 2 — Register Partner */}
        <Step completed={step2Done} active={step2Done || computedActive === 1}>
          <StepLabel>Register Partner</StepLabel>
          <StepContent>
            {existingPartnerName ? (
              <div className={classes.stepBody}>
                <Typography variant="body2"><strong>{existingPartnerName}</strong></Typography>
                {existingPartnerEmail && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {existingPartnerEmail}
                  </Typography>
                )}
                {filedAgreementTimestamp && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Registered: {moment(lastUpdated || filedAgreementTimestamp).format('MMM D, YYYY')}
                  </Typography>
                )}
              </div>
            ) : successors.length > 0 ? (
              <div className={classes.stepBody}>
                {successors.map((s, i) => (
                  <div key={i} className={classes.successorRow}>
                    <Typography variant="body2"><strong>{get(s, 'inventoryUser.name', '—')}</strong></Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {get(s, 'inventoryUser.email', '')}
                    </Typography>
                  </div>
                ))}
              </div>
            ) : userGoal ? (
              <CompletedSteps
                card={card}
                classes={classes}
                dealTrackingPipeLineId={dealTrackingPipeLineId}
                enqueueSnackbar={enqueueSnackbar}
                updateCard={updateCard}
                pipeline={pipeline}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No partner registered yet.
              </Typography>
            )}
          </StepContent>
        </Step>

        {/* Step 3 — File Agreement */}
        <Step completed={step3Done} active={step3Done || computedActive === 2}>
          <StepLabel>File Agreement</StepLabel>
          <StepContent>
            {/* Voided plans keep their artifacts. Still show them — "not yet filed"
                would be wrong (it WAS filed, then voided) and hiding the agreement
                defeats the point of retaining it. */}
            {step3Done || isVoided ? (
              <div className={classes.stepBody}>
                {isVoided && (
                  <Typography variant="body2" style={{ color: '#b91c1c' }}>
                    Agreement voided — retained below for audit.
                  </Typography>
                )}
                {filedAgreementTimestamp && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Filed: {moment(filedAgreementTimestamp).format('MMM D, YYYY h:mm A')}
                  </Typography>
                )}
                {filedAgreementDoc && (
                  <Link
                    href={filedAgreementDoc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.docLink}
                  >
                    {filedAgreementDoc.title || 'Download Agreement'}
                  </Link>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Agreement not yet filed.
              </Typography>
            )}
          </StepContent>
        </Step>

        {/* Step 4 — Approved */}
        <Step completed={step4Done} active={step4Done || canIssuedSeal || computedActive === 3}>
          <StepLabel>Approved</StepLabel>
          <StepContent>
            {/* Guard on isVoided: the card can still sit in the Approved stage
                after a void, and "approved and verified" would be a lie. */}
            {isVoided ? (
              <Typography variant="body2" style={{ color: '#b91c1c' }}>
                Approval no longer in force — plan voided.
              </Typography>
            ) : isApproved ? (
              <Typography variant="body2" className={classes.successText}>
                Agreement approved and verified.
              </Typography>
            ) : canIssuedSeal ? (
              <div className={classes.stepBody}>
                <Typography variant="body2" color="textSecondary" className={classes.sealNote}>
                  Agreement filed — ready for firm approval.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setSealDialogOpen(true)}
                >
                  Issue Verification Seal
                </Button>
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Pending agreement filing.
              </Typography>
            )}
          </StepContent>
        </Step>

      </Stepper>

      {/* Meta: dates + deal room */}
      {(accessedDate || lastUpdated || deal) && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.meta}>
            {accessedDate && (
              <Typography variant="caption" color="textSecondary" display="block">
                Accessed: {moment(accessedDate).format('MMM D, YYYY')}
              </Typography>
            )}
            {lastUpdated && (
              <Typography variant="caption" color="textSecondary" display="block">
                Last Updated: {moment(lastUpdated).format('MMM D, YYYY')}
              </Typography>
            )}
            {deal && !get(deal, 'number_of_deal_interests') && (
              <Link
                href="#"
                onClick={e => {
                  e.preventDefault();
                  window.open(`/admin/pipelines/${pipeline.id}?deal_id=${deal.id}`, '_self');
                }}
                className={classes.dealLink}
              >
                Create Deal Room
              </Link>
            )}
          </div>
        </>
      )}

      {/* Issue seal confirmation dialog */}
      <Dialog open={!!sealDialogOpen} onClose={() => setSealDialogOpen(false)}>
        <DialogTitle>Issue Verification Seal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By issuing the seal, the advisor will receive a confirmation email that their
            catastrophic plan agreement has been verified and approved by your firm.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSealDialogOpen(false)}>Cancel</Button>
          <Mutation mutation={SendSeal}>
            {issueSeal => (
              <Button
                onClick={() => handleSeal(issueSeal)}
                color="primary"
                variant="contained"
                autoFocus
              >
                Issue Seal Now
              </Button>
            )}
          </Mutation>
        </DialogActions>
      </Dialog>

    </div>
  );
};

ContinuityStepTab.propTypes = {
  card                   : PropTypes.object.isRequired,
  classes                : PropTypes.object.isRequired,
  dealTrackingPipeLineId : PropTypes.number,
  enqueueSnackbar        : PropTypes.func.isRequired,
  updateCard             : PropTypes.func.isRequired,
  pipeline               : PropTypes.object.isRequired,
};

ContinuityStepTab.defaultProps = {
  dealTrackingPipeLineId: '',
};

const styles = theme => ({
  root: {
    paddingTop: theme.spacing(1),
  },
  stageRow: {
    display       : 'flex',
    alignItems    : 'center',
    gap           : 8,
    padding       : theme.spacing(1, 2, 0),
    marginBottom  : theme.spacing(0.5),
  },
  stageLabel: {
    color          : theme.palette.text.secondary,
    textTransform  : 'uppercase',
    letterSpacing  : '0.08em',
    fontSize       : 10,
    fontWeight     : 600,
  },
  chipDefault: {
    backgroundColor : '#e3f2fd',
    color           : '#1565c0',
    fontWeight      : 600,
    fontSize        : 11,
    height          : 20,
  },
  chipFiled: {
    backgroundColor : '#fff3e0',
    color           : '#e65100',
    fontWeight      : 600,
    fontSize        : 11,
    height          : 20,
  },
  chipApproved: {
    backgroundColor : '#e8f5e9',
    color           : '#2e7d32',
    fontWeight      : 600,
    fontSize        : 11,
    height          : 20,
  },
  stepper: {
    padding         : theme.spacing(1, 0),
    backgroundColor : 'transparent',
  },
  stepBody: {
    display        : 'flex',
    flexDirection  : 'column',
    gap            : 4,
    paddingBottom  : theme.spacing(0.5),
  },
  successText: {
    color      : '#2e7d32',
    fontWeight : 500,
  },
  successorRow: {
    marginBottom: theme.spacing(0.75),
  },
  docLink: {
    fontSize    : 12,
    display     : 'block',
    marginTop   : 4,
  },
  inlineBtn: {
    marginTop   : 6,
    alignSelf   : 'flex-start',
  },
  sealNote: {
    fontSize     : 12,
    marginBottom : theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(1, 2),
  },
  meta: {
    padding        : theme.spacing(0, 2, 2),
    display        : 'flex',
    flexDirection  : 'column',
    gap            : 3,
  },
  dealLink: {
    marginTop  : 4,
    fontSize   : 12,
    cursor     : 'pointer',
  },
});

export default withStyles(styles)(withSnackbar(ContinuityStepTab));
